/**
 * provenance metadata, type helpers, and derivation constants.
 *
 * Extracted from the prototype `compass-intake.jsx:78-125`.
 * Intentionally separate from the V1 `Provenance` type in ProvenancePill —
 * the type system uses different slug names and adds `vault` / `catalog`.
 */

import type { IconProps } from "./icons";
import { Alert, Bulb, Compass, Doc } from "./icons";

/* ---------- types ---------- */

/** provenance kinds. */
export type ProvenanceType =
  | "provided"
  | "estimated"
  | "vault"
  | "catalog"
  | "empty";

/** Derivation method — how a value was produced from its references. */
export type DerivationKind = "calculated" | "quoted" | "synthesized";

/* ---------- metadata map ---------- */

export interface ProvMeta {
  label: string;
  cls: string;
  icon: (props: IconProps) => React.JSX.Element;
}

/**
 * Maps each provenance type to its display label, CSS class key,
 * and icon component.
 */
export const PROV_META: Record<Exclude<ProvenanceType, "vault">, ProvMeta> = {
  provided: { label: "Provided", cls: "prov", icon: Compass },
  estimated: { label: "External", cls: "est", icon: Bulb },
  catalog: { label: "Vault", cls: "cat", icon: Doc },
  empty: { label: "Needs input", cls: "empty", icon: Alert },
};

/* ---------- type helpers ---------- */

/** Source entry shape expected by `effType` / `derivationOf`. */
export interface CitationLike {
  type: ProvenanceType;
  sources?: Array<{ used?: boolean; kind?: string }>;
  context?: string;
  derivation?: DerivationKind;
}

/**
 * Compute the effective (badge) type for a citation.
 *
 * - Editing a source makes it `"provided"`.
 * - When references are mixed, the reference flagged `used` drives the type:
 *   a catalog `used` ref → `"catalog"`, an external one → `"estimated"`.
 */
export function effType(
  c: CitationLike,
  edit: boolean,
): Exclude<ProvenanceType, "vault"> {
  if (edit) return "provided";
  if (c.type === "empty") return "empty";
  if ((c.type === "estimated" || c.type === "catalog") && c.sources) {
    const u = c.sources.find((s) => s.used);
    if (u?.kind === "catalog") return "catalog";
    if (u?.kind === "external") return "estimated";
  }
  // Fall through: if type is "vault", treat as "catalog"; otherwise use as-is
  if (c.type === "vault") return "catalog";
  return c.type as Exclude<ProvenanceType, "vault">;
}

/**
 * Determine how the exact number or claim was produced from its references.
 *
 * - `null` for provided / empty values (no derivation applies).
 * - `"calculated"` for numeric values (always a single reference).
 * - `"quoted"` or `"synthesized"` for contextual claims.
 */
export function derivationOf(c: CitationLike | null): DerivationKind | null {
  if (!c || c.type === "provided" || c.type === "empty") return null;
  if (!c.context) return "calculated";
  return (
    c.derivation ??
    (c.sources && c.sources.length > 1 ? "synthesized" : "quoted")
  );
}

/* ---------- derivation display constants ---------- */

export const DERIV_LABEL: Record<DerivationKind, string> = {
  calculated: "Derived",
  quoted: "Quoted",
  synthesized: "Synthesized",
};

export const DERIV_CLS: Record<DerivationKind, string> = {
  calculated: "c",
  quoted: "q",
  synthesized: "s",
};

export const DERIV_INFO: Record<DerivationKind, string> = {
  calculated:
    "Derived from a single reference; that reference's type sets the source type.",
  quoted: "Quoted from a single reference, shown verbatim.",
  synthesized: "Synthesized from one or multiple references, restated.",
};

/* ---------- type info tooltips ---------- */

/** Succinct hover summaries for the AI-derived type badges. */
export const TYPE_INFO: Record<string, string> = {
  provided:
    "Entered by a person at your organization. Editing any source turns it into a Provided value.",
  estimated: "High-quality web results that should be double-checked.",
  catalog:
    "Sourced from our vault: independent research alongside data provided directly from vendors on our marketplace.",
};
